import type { QuizAnswerValidationResult, QuizFetchResponse } from "@/types/quiz.types";

export default function useQuiz () {
  const isLoading = ref(false);
  const fetchError = ref(null as any | null);

  const { dispatchMessage } = useSystemMessages();

  const fetchQuiz = async (quizId: string) => {
    return await useErrorHandling(async () => {
      if (!quizId) {
        throw new Error("Quiz ID is required to get course.");
      }

      isLoading.value = true;

      const response = await useAuthenticatedFetch<QuizFetchResponse>(useApiUrl(`/quizzes/${quizId}`));
      const quizFetchResponse = response.data.value;

      if (response.error.value) {
        fetchError.value = response.error;
      }

      if (!response.data?.value || !quizFetchResponse?.quiz) {
        throw new Error("Invalid response from server.");
      }

      isLoading.value = false;

      return { ...quizFetchResponse.quiz, quizId };
    }, 'Fetching quiz failed');
  };

  const validateQuestionAnswer = async (quizId: string, questionId: number | undefined, answerIds: number[]) => {
    if (answerIds.length === 0) {
      return false;
    }

    return useErrorHandling(async () => {
      const response = await useAuthenticatedFetch<QuizAnswerValidationResult>(
        useApiUrl(`/quizzes/${quizId}/questions/${questionId}/validate`),
        {
          method: "POST",
          body: { answerIds },
        },
      );

      if (response.error.value) {
        dispatchMessage('Error!', 'error', 'Validating quiz answer failed.');
      }

      return response.data;
    }, 'Validating quiz answer failed');
  };

  const resetQuiz = async (quizId: string) => {
    return await useErrorHandling(async () => {
      return await useAuthenticatedFetch<QuizAnswerValidationResult>(
        useApiUrl(`/quizzes/${quizId}/abort`),
        {
          method: "POST",
        },
      );
    }, 'Resetting quiz failed');
  };

  return {
    fetchError,
    isLoading,

    fetchQuiz,
    resetQuiz,
    validateQuestionAnswer,
  };
}
